import { gql, useLazyQuery, useMutation } from '@apollo/client'
import FacebookIcon from '@mui/icons-material/Facebook'
import InstagramIcon from '@mui/icons-material/Instagram'
import LocationOffIcon from '@mui/icons-material/LocationOff'
import XIcon from '@mui/icons-material/X'
import { TableContainer } from '@mui/material'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Grid from '@mui/material/Grid'
import Icon from '@mui/material/Icon'
import { lighten } from '@mui/material/styles'
import Switch from '@mui/material/Switch'
import Tab from '@mui/material/Tab'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tabs from '@mui/material/Tabs'
import Typography from '@mui/material/Typography'
import {
  Brand,
  Retailer,
  Query,
  Mutation,
  Location,
  FacebookPage,
  RetailerAccount,
  TwitterAccount,
} from '@promoboxx/graphql-gateway-types'
import I18n from 'i18n-js'
import { useSnackbar } from 'notistack'
import { useEffect, useMemo, useState } from 'react'
import { makeStyles } from 'tss-react/mui'

import RequestCampaignsDialog from '@src/components/content/RequestCampaignsDialog'
import { FacebookPermissionsDialog } from '@src/components/shared/FacebookPermissions/FacebookPermissionsDialog'
import LinkChild from '@src/lib/LinkChild'
import { useFeatureFlag } from '@src/lib/useFeatureFlag'
import useIsMobileBreakpoint from '@src/lib/useIsMobileBreakpoint'

import cloudinary from '../../lib/Cloudinary'
import { createR1UrlBasePath } from '../../lib/Utils'
import retailerSettingsPaths from '../retailerSettings/retailerSettingsPaths'
import GoogleMap from '../shared/GoogleMap'
import SmartImage from '../shared/SmartImage'

import ManageBrandCustomFields from './ManageBrandCustomFields'
import { useManageBrandDialogContext } from './manageBrandsDialogContext'
import manageBrandsPaths from './manageBrandsPaths'

const useStyles = makeStyles()((theme) => ({
  image: {
    width: 200,
    height: 80,
    objectFit: 'contain',
  },
  facebookIcon: {
    color: theme.palette.facebook.main,
  },
  xIcon: {
    marginRight: theme.spacing(2),
  },
  instagramIcon: {
    color: theme.palette.instagram.main,
    marginRight: theme.spacing(2),
  },
  moreContent: {
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  connectedAccountsWarning: {
    marginTop: '1em',
  },
  instagramListIcon: {
    marginRight: theme.spacing(2),
    color: theme.palette.text.secondary,
    verticalAlign: 'middle',
    opacity: theme.palette.action.disabledOpacity,
  },
  instagramListIconEnabled: {
    color: theme.palette.instagram.main,
    opacity: 1,
  },
  missingConnection: {
    color: theme.palette.warning.contrastText,
    // Copying what Alert does.
    // https://github.com/mui/material-ui/blob/a563a60219f7f6519fb0f34f6d8e3bf0974e6495/packages/material-ui-lab/src/Alert/Alert.js#L46
    backgroundColor: lighten(theme.palette.warning.main, 0.9),
  },
  tabs: {
    background: theme.palette.background.paper,
    marginBottom: theme.spacing(1),
  },
  tabContent: {
    minHeight: 500,
    [theme.breakpoints.down('sm')]: {
      minHeight: 300,
    },
  },
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
  },
  socialAccounts: {
    width: '100%',
  },
  requestMoreButton: {
    margin: theme.spacing(2, 0),
  },
  brandContainer: {
    [theme.breakpoints.only('xs')]: {
      marginTop: theme.spacing(3),
    },
  },
}))

export type ManageBrandTab = 'social' | 'locations' | 'custom_fields'

interface ManageBrandProps {
  brandId?: Brand['id']
  retailerId: Retailer['id']
  initialTab?: ManageBrandTab
  subtitle?: string
  onClose: () => void
}

const ManageBrand: React.FC<React.PropsWithChildren<ManageBrandProps>> = ({
  retailerId,
  initialTab = 'social',
  onClose,
  subtitle,
}) => {
  const { classes } = useStyles()
  const params = manageBrandsPaths.index.useParams()
  const [isRequestDialogOpen, setIsRequestDialogOpen] = useState(false)
  const [selectedTab, setSelectedTab] = useState<
    'social' | 'locations' | 'custom_fields'
  >(
    ['social', 'locations', 'custom_fields'].includes(initialTab)
      ? initialTab
      : 'social',
  )
  const isMobile = useIsMobileBreakpoint()

  const [getRetailer, { data, refetch }] = useLazyQuery<Query>(GET_RETAILER, {
    fetchPolicy: 'cache-and-network',
  })

  useEffect(() => {
    getRetailer({
      variables: {
        id: retailerId,
        retailerAccountId: params.retailerAccountSlug,
        params: { fields: ['facebook_pages'] },
        retailerAccountFields: [
          'facebook_pages',
          'twitter_accounts',
          'facebook_pages__remote_token_status',
        ],
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.brandSlug, params.retailerAccountSlug, selectedTab])

  const retailer = data?.getRetailer
  const retailerAccount = data?.getRetailerAccount
  let children: JSX.Element | null = null
  if (retailer && retailerAccount) {
    switch (selectedTab) {
      case 'social':
        children = (
          <SocialAccounts
            facebookPages={retailer.facebook_pages || []}
            twitterAccounts={retailer.twitterAccounts || []}
            retailerAccount={retailerAccount}
            retailerId={retailer?.id}
            reloadHook={refetch}
          />
        )
        break
      case 'locations':
        children = (
          <Locations
            retailerId={retailer.id}
            retailerLocations={retailer.locations}
            retailerAccountLocations={retailerAccount.locations || []}
          />
        )
        break
      default:
        children = (
          <ManageBrandCustomFields
            retailerId={retailerId}
            retailerAccountId={retailerAccount.id}
            brandId={retailer?.brand?.id}
          />
        )
        break
    }
  }

  const { isValid } = useManageBrandDialogContext()

  return (
    <>
      <DialogTitle>
        <Box
          display="flex"
          alignItems="center"
          justifyContent={{ xs: 'center', sm: 'space-between' }}
          flexWrap={{ xs: 'wrap', sm: 'nowrap' }}
        >
          <Box
            display="flex"
            alignItems="center"
            className={classes.brandContainer}
          >
            <SmartImage
              alt={retailer?.brand?.name || ''}
              className={classes.image}
              fallbackSrc={cloudinary
                .instance()
                .url('preview-defaults/campaign-default')}
              src={retailer?.brand?.logo_url}
            />
            <div>
              <Typography variant={isMobile ? 'h5' : 'h4'}>
                {retailer?.brand?.name}
              </Typography>
              <Typography variant="subtitle1" color="textSecondary">
                {subtitle}
              </Typography>
            </div>
          </Box>

          <Button
            onClick={() => setIsRequestDialogOpen(true)}
            variant="outlined"
            className={classes.requestMoreButton}
          >
            {I18n.t('content.requestMore')}
          </Button>
        </Box>
      </DialogTitle>
      <Tabs
        value={selectedTab}
        onChange={(_event, value) => setSelectedTab(value)}
        className={classes.tabs}
      >
        <Tab value={'social'} label={I18n.t('manageBrands.socialAccounts')} />
        <Tab value={'locations'} label={I18n.t('manageBrands.locations')} />
        <Tab
          value={'custom_fields'}
          label={I18n.t('manageBrands.brandQuestions')}
        />
      </Tabs>
      <DialogContent className={classes.dialogContent}>
        <Box className={classes.tabContent}>
          {children || (
            <Box display="flex" pt={30} justifyContent="center">
              <CircularProgress />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          {I18n.t('shared.close')}
        </Button>
        <Button
          variant="contained"
          onClick={onClose}
          color="primary"
          disabled={!isValid}
        >
          {I18n.t('shared.done')}
        </Button>
      </DialogActions>
      {
        <RequestCampaignsDialog
          brandId={retailer?.brand?.id}
          open={isRequestDialogOpen}
          onClose={() => setIsRequestDialogOpen(false)}
        />
      }
    </>
  )
}

interface SocialAccountsProps {
  facebookPages: FacebookPage[]
  retailerAccount: RetailerAccount
  retailerId: Retailer['id']
  twitterAccounts: TwitterAccount[]
  reloadHook: () => void
}

const SocialAccounts: React.FC<
  React.PropsWithChildren<SocialAccountsProps>
> = ({
  facebookPages: retailerFacebookPages,
  twitterAccounts: retailerTwitterAccounts,
  retailerAccount,
  retailerId,
  reloadHook,
}) => {
  const { classes, cx } = useStyles()
  const [isFacebookPermissionsDialogOpen, setIsFacebookPermissionsDialogOpen] =
    useState(false)
  const params = manageBrandsPaths.index.useParams()

  const handleManageFacebookClick = () => {
    setIsFacebookPermissionsDialogOpen(true)
  }

  // These have to be memoized due to the empty array and the fact that they're
  // used as dependencies.
  const facebookPages = useMemo(
    () => retailerAccount.facebook_pages || [],
    [retailerAccount.facebook_pages],
  )
  const twitterAccounts = useMemo(
    () => retailerAccount.twitter_accounts || [],
    [retailerAccount.twitter_accounts],
  )

  const { setIsValid, validationMode, shouldHighlightMissing } =
    useManageBrandDialogContext()

  const isShareflowUpliftEnabled = useFeatureFlag('share-flow-uplift')

  // These have to be done in the one effect, otherwise one overwrites the
  // other.
  useEffect(() => {
    let isValid = true

    if (validationMode === 'facebook') {
      isValid = facebookPages.length > 0 && retailerFacebookPages.length > 0
    } else if (validationMode === 'twitter') {
      isValid = twitterAccounts.length > 0 && retailerTwitterAccounts.length > 0
    }

    setIsValid(isValid)
  }, [
    facebookPages,
    retailerFacebookPages,
    twitterAccounts,
    retailerTwitterAccounts,
    setIsValid,
    validationMode,
  ])

  return (
    <div>
      <Grid className={classes.socialAccounts} container spacing={6}>
        <Grid item xs={12} md={6}>
          <Box display="flex" p="2" alignItems="center">
            <FacebookIcon className={classes.facebookIcon} fontSize="large" />
            <InstagramIcon className={classes.instagramIcon} fontSize="large" />
            <Typography variant="h6">
              {I18n.t('manageBrands.facebookInstagram')}
            </Typography>
            <Box flex={1} />
            <Button
              color="primary"
              size="small"
              variant="outlined"
              onClick={handleManageFacebookClick}
            >
              {I18n.t('manageBrands.manage')}
            </Button>
          </Box>
          {facebookPages.length > 0 && retailerFacebookPages.length === 0 ? (
            <Alert
              severity="warning"
              className={classes.connectedAccountsWarning}
            >
              {I18n.t('manageBrands.noFacebookPagesConnectedWarning')}
            </Alert>
          ) : undefined}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align="left">
                    {I18n.t('manageBrands.page')}
                  </TableCell>
                  <TableCell align="right" width={120}>
                    {I18n.t('manageBrands.connected')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {facebookPages.length > 0 ? (
                  facebookPages.map((retailerAccountFacebookPage, i) => {
                    const retailerFacebookPage = retailerFacebookPages.find(
                      (page) => retailerAccountFacebookPage.id === page.id,
                    )
                    return (
                      // eslint-disable-next-line react/no-array-index-key
                      <TableRow key={`facebookPage-${i}`}>
                        <TableCell
                          align="left"
                          className={cx({
                            [classes.missingConnection]:
                              shouldHighlightMissing && !retailerFacebookPage,
                          })}
                        >
                          <Icon
                            className={cx(classes.instagramListIcon, {
                              [classes.instagramListIconEnabled]: Boolean(
                                retailerAccountFacebookPage.instagram_business_account_id,
                              ),
                            })}
                          >
                            <InstagramIcon />
                          </Icon>
                          {retailerAccountFacebookPage.name}
                        </TableCell>
                        <TableCell
                          align="right"
                          className={cx({
                            [classes.missingConnection]:
                              shouldHighlightMissing && !retailerFacebookPage,
                          })}
                        >
                          <ConnectedFacebookSwitch
                            retailerId={retailerId}
                            retailerFacebookPage={retailerFacebookPage}
                            retailerAccountFacebookPage={
                              retailerAccountFacebookPage
                            }
                          />
                        </TableCell>
                      </TableRow>
                    )
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={2} align="center">
                      <Typography color="textSecondary" gutterBottom>
                        {I18n.t('manageBrands.noAccountsFound')}
                      </Typography>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleManageFacebookClick}
                      >
                        {I18n.t('manageBrands.addFacebookPage')}
                      </Button>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box display="flex" p="2" alignItems="center">
            <XIcon className={classes.xIcon} fontSize="large" />
            <Typography variant="h6">{I18n.t('filters.twitter')}</Typography>
            <Box flex={1} />
            {isShareflowUpliftEnabled ? (
              <LinkChild
                to={retailerSettingsPaths.socialAccounts.build({
                  retailerAccountSlug: params.retailerAccountSlug,
                })}
              >
                <Button color="primary" size="small" variant="outlined">
                  {I18n.t('manageBrands.manage')}
                </Button>
              </LinkChild>
            ) : (
              <Button
                color="primary"
                size="small"
                variant="outlined"
                href={`/#${createR1UrlBasePath(
                  params.retailerAccountSlug,
                  params.brandSlug,
                )}settings?tab=social_accounts`}
                target="_blank"
              >
                {I18n.t('manageBrands.manage')}
              </Button>
            )}
          </Box>
          {twitterAccounts.length > 0 &&
          retailerTwitterAccounts.length === 0 ? (
            <Alert
              severity="warning"
              className={classes.connectedAccountsWarning}
            >
              {I18n.t('manageBrands.noTwitterAccountsConnectedWarning')}
            </Alert>
          ) : undefined}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align="left">
                    {I18n.t('manageBrands.page')}
                  </TableCell>
                  <TableCell align="right" width={120}>
                    {I18n.t('manageBrands.connected')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {twitterAccounts.length > 0 ? (
                  twitterAccounts.map((retailerAccountTwitterAccount) => {
                    const retailerTwitterAccount = retailerTwitterAccounts.find(
                      (account) =>
                        retailerAccountTwitterAccount?.id === account.id,
                    )
                    return (
                      <TableRow
                        key={`twitterAccounts-${retailerAccountTwitterAccount?.name}`}
                        className={cx({
                          [classes.missingConnection]:
                            shouldHighlightMissing && !retailerTwitterAccount,
                        })}
                      >
                        <TableCell align="left">
                          {retailerAccountTwitterAccount?.name}
                        </TableCell>
                        <TableCell align="right">
                          <ConnectedTwitterSwitch
                            retailerId={retailerId}
                            retailerTwitterAccount={retailerTwitterAccount}
                            retailerAccountTwitterAccount={
                              retailerAccountTwitterAccount
                            }
                          />
                        </TableCell>
                      </TableRow>
                    )
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={2} align="center">
                      <Typography color="textSecondary" gutterBottom>
                        {I18n.t('manageBrands.noAccountsFound')}
                      </Typography>
                      {isShareflowUpliftEnabled ? (
                        <LinkChild
                          to={retailerSettingsPaths.socialAccounts.build({
                            retailerAccountSlug: params.retailerAccountSlug,
                          })}
                        >
                          <Button variant="contained" color="primary">
                            {I18n.t('manageBrands.addTwitterAccount')}
                          </Button>
                        </LinkChild>
                      ) : (
                        <Button
                          variant="contained"
                          color="primary"
                          href={`/#${createR1UrlBasePath(
                            params.retailerAccountSlug,
                            params.brandSlug,
                          )}settings?tab=social_accounts`}
                          target="_blank"
                        >
                          {I18n.t('manageBrands.addTwitterAccount')}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
      <FacebookPermissionsDialog
        initialFacebookPageId={null}
        open={isFacebookPermissionsDialogOpen}
        handleClose={(pages) => {
          // check for diff in returned pages and reload
          if (
            !pages.length ||
            pages.length !== facebookPages.length ||
            facebookPages.filter(
              (fbPage) =>
                !pages.some((dialogPage) => dialogPage.id === fbPage.id),
            ).length
          ) {
            reloadHook()
          }
          setIsFacebookPermissionsDialogOpen(false)
        }}
      />
    </div>
  )
}

interface ConnectedFacebookSwitchProps {
  retailerAccountFacebookPage: FacebookPage
  retailerFacebookPage: FacebookPage | undefined
  retailerId: Retailer['id']
}
const ConnectedFacebookSwitch: React.FC<
  React.PropsWithChildren<ConnectedFacebookSwitchProps>
> = ({ retailerFacebookPage, retailerAccountFacebookPage, retailerId }) => {
  const { enqueueSnackbar } = useSnackbar()
  const [removeFacebookPage] = useMutation<Mutation>(DISCONNECT_FACEBOOK_PAGE)
  const [addFacebookPage] = useMutation<Mutation>(CONNECT_FACEBOOK_PAGE)

  const [connectedFacebookPage, setConnectedFacebookPage] = useState<
    FacebookPage | undefined
  >(retailerFacebookPage)
  const handleConnectPage = async () => {
    setConnectedFacebookPage(retailerAccountFacebookPage)
    const {
      uid,
      graph_id,
      name,
      link,
      primary,
      likes,
      sharable,
      authenticated_remote,
      auto_share_enabled,
    } = retailerAccountFacebookPage
    try {
      await addFacebookPage({
        variables: {
          resourceType: 'retailers',
          resourceId: retailerId,
          facebookPage: {
            uid,
            graph_id,
            name,
            link,
            primary,
            likes,
            sharable,
            authenticated_remote,
            auto_share_enabled,
          },
        },
        refetchQueries: [GET_RETAILER],
      })
    } catch (e) {
      enqueueSnackbar(I18n.t('manageBrands.addPageError'), {
        variant: 'error',
      })
      setConnectedFacebookPage(undefined)
    }
  }
  const handleRemovePage = () => {
    setConnectedFacebookPage(undefined)
    try {
      removeFacebookPage({
        variables: {
          resourceType: 'retailers',
          resourceId: retailerId,
          facebookPageId: connectedFacebookPage?.id,
        },
        refetchQueries: [GET_RETAILER],
      })
    } catch (e) {
      enqueueSnackbar(I18n.t('manageBrands.removePageError'), {
        variant: 'error',
      })
      setConnectedFacebookPage(retailerFacebookPage)
    }
  }
  return (
    <Switch
      checked={!!connectedFacebookPage}
      onChange={(e, checked) =>
        checked ? handleConnectPage() : handleRemovePage()
      }
    />
  )
}

interface ConnectedTwitterSwitchProps {
  retailerAccountTwitterAccount: TwitterAccount
  retailerTwitterAccount: TwitterAccount | undefined
  retailerId: Retailer['id']
}
const ConnectedTwitterSwitch: React.FC<
  React.PropsWithChildren<ConnectedTwitterSwitchProps>
> = ({ retailerTwitterAccount, retailerAccountTwitterAccount, retailerId }) => {
  const { enqueueSnackbar } = useSnackbar()
  const [disconnectTwitterAccount] = useMutation<Mutation>(
    DISCONNECT_TWITTER_ACCOUNT,
  )
  const [connectTwitterAccount] = useMutation<Mutation>(CONNECT_TWITTER_ACCOUNT)

  const [connectedTwitterAccount, setConnectedTwitterAccount] = useState<
    TwitterAccount | undefined
  >(retailerTwitterAccount)
  const handleConnectAccount = async () => {
    setConnectedTwitterAccount(retailerAccountTwitterAccount)
    const {
      id,
      uid,
      nickname,
      name,
      token,
      secret,
      created_at,
      updated_at,
      location,
      follower_count,
      listed_count,
      statuses_count,
      profile_image_url,
      synced_at,
      description,
      primary,
      deleted_at,
      created_by_id,
      updated_by_id,
      url,
      auto_share_enabled,
      has_been_authenticated,
      sharable,
      automated,
      authenticated_local,
      parent_id,
    } = retailerAccountTwitterAccount
    try {
      await connectTwitterAccount({
        variables: {
          resourceType: 'retailers',
          resourceId: retailerId,
          twitterAccount: {
            id,
            uid,
            nickname,
            name,
            token,
            secret,
            created_at,
            updated_at,
            location,
            follower_count,
            listed_count,
            statuses_count,
            profile_image_url,
            synced_at,
            description,
            primary,
            deleted_at,
            created_by_id,
            updated_by_id,
            url,
            auto_share_enabled,
            has_been_authenticated,
            sharable,
            automated,
            authenticated_local,
            parent_id,
          },
        },
        refetchQueries: [GET_RETAILER],
      })
    } catch (e) {
      enqueueSnackbar(I18n.t('manageBrands.addAccountError'), {
        variant: 'error',
      })
      setConnectedTwitterAccount(undefined)
    }
  }
  const handleRemoveAccount = () => {
    setConnectedTwitterAccount(undefined)
    try {
      disconnectTwitterAccount({
        variables: {
          resourceType: 'retailers',
          resourceId: retailerId,
          id: connectedTwitterAccount?.id,
        },
        refetchQueries: [GET_RETAILER],
      })
    } catch (e) {
      enqueueSnackbar(I18n.t('manageBrands.removeAccountError'), {
        variant: 'error',
      })
      setConnectedTwitterAccount(retailerTwitterAccount)
    }
  }
  return (
    <Switch
      checked={!!connectedTwitterAccount}
      onChange={(e, checked) =>
        checked ? handleConnectAccount() : handleRemoveAccount()
      }
    />
  )
}

interface LocationsProps {
  retailerLocations: Location[]
  retailerAccountLocations: Location[]
  retailerId: Retailer['id']
}
const Locations: React.FC<React.PropsWithChildren<LocationsProps>> = ({
  retailerLocations,
  retailerAccountLocations,
  retailerId,
}) => {
  const { enqueueSnackbar } = useSnackbar()
  const [addLocation] = useMutation<Mutation>(CONNECT_LOCATION)
  const [removeLocation] = useMutation<Mutation>(DISCONNECT_LOCATION)
  const [currentLocations, setCurrentLocations] =
    useState<Location[]>(retailerLocations)

  const { classes, cx } = useStyles()

  const { validationMode, setIsValid, shouldHighlightMissing } =
    useManageBrandDialogContext()

  useEffect(() => {
    let isValid = true
    if (validationMode === 'locations') {
      isValid =
        retailerAccountLocations.length > 0 && currentLocations.length > 0
    }

    setIsValid(isValid)
  }, [currentLocations, retailerAccountLocations, setIsValid, validationMode])

  const handleAddLocation = async (location: Location) => {
    const { __typename, id, ...newLocation } = location

    newLocation.locationable_id = retailerId
    newLocation.locationable_type = 'Retailer'
    newLocation.parent_id = location.id

    try {
      const createdLocation = await addLocation({
        variables: {
          resourceType: 'retailers',
          resourceId: retailerId,
          location: newLocation,
        },
      })
      if (createdLocation?.data?.createLocation) {
        const location: Location = {
          id: createdLocation.data.createLocation.id,
          __typename,
          ...newLocation,
        }
        setCurrentLocations([...currentLocations, location])
      }
    } catch (e) {
      enqueueSnackbar(I18n.t('manageBrands.addLocationError'), {
        variant: 'error',
      })
    }
  }

  const handleRemoveLocation = async (location: Location) => {
    const locationToRemove = currentLocations.find(
      (retailerLocation) => retailerLocation.parent_id === location.id,
    )

    if (locationToRemove) {
      const filteredLocations = currentLocations.filter(
        (loc) => loc.parent_id !== locationToRemove.parent_id,
      )
      setCurrentLocations(filteredLocations)
      try {
        await removeLocation({
          variables: {
            id: locationToRemove.id,
          },
        })
      } catch (e) {
        enqueueSnackbar(I18n.t('manageBrands.removeLocationError'), {
          variant: 'error',
        })
        setCurrentLocations([...filteredLocations, locationToRemove])
      }
    }
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="left">Address</TableCell>
                <TableCell align="right">Connected</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {retailerAccountLocations.map((location) => (
                <TableRow
                  className={cx({
                    [classes.missingConnection]:
                      shouldHighlightMissing &&
                      !currentLocations.some(
                        (currentLocation) =>
                          currentLocation.parent_id === location.id,
                      ),
                  })}
                >
                  <TableCell align="left">
                    {location.addr1}
                    {', '}
                    {location.city}
                    {', '}
                    {location.state}
                  </TableCell>
                  <TableCell align="right">
                    <Switch
                      checked={currentLocations.some(
                        (retailerLocation) =>
                          retailerLocation.parent_id === location.id,
                      )}
                      onChange={async (e, checked) => {
                        if (checked) {
                          handleAddLocation(location)
                        } else {
                          handleRemoveLocation(location)
                        }
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
      <Grid item xs={12} md={6}>
        {currentLocations.length ? (
          <GoogleMap
            style={{ height: '400px' }}
            locations={currentLocations.map((location) => ({
              latitude: location.latitude || '',
              longitude: location.longitude || '',
            }))}
          />
        ) : (
          <Box
            p={6}
            display="flex"
            height="100%"
            alignItems="center"
            justifyContent="center"
            flexDirection="column"
          >
            <LocationOffIcon color="disabled" fontSize="large" />
            <Typography color="textSecondary">
              {I18n.t('manageBrands.noLocations')}
            </Typography>
          </Box>
        )}
      </Grid>
    </Grid>
  )
}

export default ManageBrand

const DISCONNECT_LOCATION = gql`
  mutation removeRetailerLocation($id: ID!) {
    deleteLocation(id: $id)
  }
`

const CONNECT_LOCATION = gql`
  mutation addRetailerLocation(
    $resourceType: LocationResourceType!
    $resourceId: ID!
    $location: LocationInput!
  ) {
    createLocation(
      resourceId: $resourceId
      resourceType: $resourceType
      location: $location
    ) {
      id
    }
  }
`
const CONNECT_FACEBOOK_PAGE = gql`
  mutation connectFacebookPage(
    $resourceType: String!
    $resourceId: ID!
    $facebookPage: CreateFacebookPageInput!
  ) {
    createFacebookPage(
      resourceType: $resourceType
      resourceId: $resourceId
      facebookPage: $facebookPage
    ) {
      id
    }
  }
`

const DISCONNECT_FACEBOOK_PAGE = gql`
  mutation disconnectFacebookPage(
    $facebookPageId: ID!
    $resourceId: ID!
    $resourceType: FacebookPageResourceType!
  ) {
    deleteResourceSpecificFacebookPage(
      resourceId: $resourceId
      resourceType: $resourceType
      facebookPageId: $facebookPageId
    )
  }
`
const CONNECT_TWITTER_ACCOUNT = gql`
  mutation connectTwitterAccount(
    $resourceType: String!
    $resourceId: ID!
    $twitterAccount: CreateTwitterAccountInput!
  ) {
    createTwitterAccount(
      resourceType: $resourceType
      resourceId: $resourceId
      twitterAccount: $twitterAccount
    ) {
      id
    }
  }
`

export const DISCONNECT_TWITTER_ACCOUNT = gql`
  mutation disconnectTwitterAccount(
    $resourceType: String!
    $resourceId: ID!
    $id: ID!
  ) {
    deleteTwitterAccount(
      resourceType: $resourceType
      resourceId: $resourceId
      id: $id
    )
  }
`

const GET_RETAILER = gql`
  query getRetailerManageBrand(
    $id: ID!
    $retailerAccountId: ID!
    $params: GetRetailerParams
    $retailerAccountFields: [String!]
  ) {
    getRetailerAccount(id: $retailerAccountId, fields: $retailerAccountFields) {
      id
      locations {
        id
        addr1
        addr2
        city
        state
        zip
        country_code
        latitude
        longitude
        locationable_id
      }
      facebook_pages {
        id
        graph_id
        uid
        name
        link
        permissions
        updated_at
        token_expires_at
        last_auth_error {
          response_body {
            error {
              type
              message
              code
            }
          }
        }
        deleted_at
        primary
        likes
        picture_url
        verified
        authenticated_local
        instagram_actor_id
        instagram_business_account_id
        business_manager_permissions
        paid_ads_permissions_enabled
        system_token
        facebook_users_and_roles {
          name
          id
          tasks
          is_active
        }
        automations {
          id
          retailer_id
          category
          automationable_id
          automationable_type
          executed_at
          created_at
          updated_at
          failed_at
          deleted_at
          enabled
          status
        }
        remote_token_status {
          app_id
          type
          application
          data_access_expires_at
          expires_at
          is_valid
          issued_at
          scopes
          granular_scopes {
            scope
            target_ids
          }
          user_id
        }
      }
      twitter_accounts {
        id
        uid
        nickname
        name
        token
        secret
        created_at
        updated_at
        location
        follower_count
        listed_count
        statuses_count
        profile_image_url
        synced_at
        description
        primary
        deleted_at
        created_by_id
        updated_by_id
        url
        auto_share_enabled
        has_been_authenticated
        sharable
        automated
        authenticated_local
        parent_id
      }
    }
    getRetailer(id: $id, params: $params) {
      id
      name
      brand {
        name
        logo_url
        id
      }
      customFieldValues {
        id
        custom_field {
          id
          required
        }
        value
      }
      customFields {
        id
        slug
        input_type
        brand_id
        created_at
        updated_at
        preferred
        link_parameter_name
        display_name
        required
        unique
        retailer_permission
        deleted_at
        custom_fieldable_type
        choices
        custom_field_set_id
        field_type
      }
      facebook_pages {
        id
        instagram_actor_id
        business_manager_permissions
        paid_ads_permissions_enabled
      }
      twitterAccounts {
        id
        has_been_authenticated
      }
      locations {
        id
        addr1
        addr2
        city
        state
        zip
        country_code
        latitude
        longitude
        parent_id
        locationable_id
      }
    }
  }
`
